import { Actions } from 'vuex-smart-module';
import { ResponseStatuses } from '@/enums';
import { AxiosResponse } from 'axios';
import { TableRequest } from '@/types';
import { LeadsService } from '../services';
import LeadsState from './state';
import LeadsMutations from './mutations';
import LeadsGetters from './getters';
import { Leads } from '../types';

const leadsService = new LeadsService();

export default class LeadsActions extends Actions<
  LeadsState,
  LeadsGetters,
  LeadsMutations,
  // eslint-disable-next-line no-use-before-define
  LeadsActions
> {
  async get_leads(storage_limit?: number) {
    this.mutations.SET_LOADING(true);

    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;

      const filtersEmpty = this.state.statuses.length < 1;

      const payload: TableRequest<Leads.RequestFilters> = {
        filters: this.state.filters,
        filters_exist: !filtersEmpty,
        pagination: {
          limit: storage_limit ?? itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta } = await leadsService.get_leads(payload);

      this.mutations.SET_ITEMS(data.items);

      if (filtersEmpty) {
        this.mutations.SET_TABLE_FILTERS(data.filters);
      }

      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });

      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.SET_LOADING(false);
    }
  }

  async get_lead_by_id(id: string | number) {
    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await leadsService.get_lead_by_id(id);

      this.mutations.SET_CURRENT_LEAD(data.item);
      this.mutations.SET_IS_READ_ONLY(data.is_read_only);
      this.mutations.SET_TRANSITION_STATUSES(data.available_transition_statuses);
      return code;
    } catch (error) {
      console.error(error);

      if ((error as AxiosResponse).data.message) {
        this.mutations.SET_RESERVED_LEAD_MESSAGE((error as AxiosResponse).data.message || '');
      }
      return (error as AxiosResponse).data.code;
    }
  }

  async get_lead_logs(id: string | number) {
    try {
      const { data } = await leadsService.get_lead_logs(id);

      this.mutations.SET_CURRENT_LEAD_LOGS(data);
    } catch (error) {
      console.log(error);
    }
  }

  // Добавление комментария в лог лида
  async add_lead_log(text_log: string) {
    const { id } = this.state.current_lead;
    const payload = { comment: text_log };

    if (!id) {
      return;
    }

    try {
      const { status } = await leadsService.add_lead_log(id, payload);

      if (status === ResponseStatuses.Success.CREATED) {
        await this.dispatch('get_lead_logs', id);
        return status;
      }
    } catch (error) {
      console.log(error);
    }
  }

  async upd_lead(payload: Leads.UpdLeadPayload) {
    try {
      const { code = ResponseStatuses.Success.UPDATED } = await leadsService.upd_lead(payload);

      if (code === ResponseStatuses.Success.UPDATED) {
        await this.dispatch('get_lead_logs', payload.id);
        await this.dispatch('get_lead_by_id', payload.id);
      }

      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async reserve_lead_page(id: string | number) {
    try {
      await leadsService.reserve_lead_page(id);
    } catch (error) {
      console.log(error);
    }
  }

  async unreserve_lead_page(id: string | number) {
    try {
      await leadsService.unreserve_lead_page(id);
    } catch (error) {
      console.log(error);
    }
  }

  async add_lead_reminder(payload: Leads.LeadReminder) {
    const { id } = this.state.current_lead;

    if (!id) {
      return;
    }

    try {
      const data = await leadsService.add_lead_reminder(id, payload);

      if (data.code === ResponseStatuses.Success.CREATED) {
        await this.dispatch('get_lead_by_id', id);
        await this.dispatch('get_lead_logs', id);
      }

      return data.code;
    } catch (error) {
      console.log(error);
    }
  }

  async delete_lead_reminder(reminder_id: string | number) {
    const { id } = this.state.current_lead;

    if (!reminder_id || !id) {
      return;
    }

    try {
      const data = await leadsService.delete_lead_reminder(reminder_id);

      if (data.code === ResponseStatuses.Success.UPDATED) {
        await this.dispatch('get_lead_by_id', id);
        await this.dispatch('get_lead_logs', id);
      }
    } catch (error) {
      console.log(error);
    }
  }
}
